import { createSelector } from "reselect";
import { defaultSource as processDataViewerDefaultSource } from "pages/RS-Workspace/components/Results/components/Elements/components/ProcessDataViewerElement";
import { defaultSource as lineGraphDefaultSource } from "pages/RS-Workspace/components/Results/components/Elements/components/LineGraphElement";
import { getDefaultUnitsPrefix } from "shared/rsDictionary";
import { parameterTypesSelector, processVariablesOptionsSelector, processVariablesSelector, } from "store/global/global.selectors";
import { compareResultItemIds } from "utils/data";
import { isNotEmpty } from "utils/validate";
import { CalculationElementSourceType, CalculationStatusSource, ResultElementParameterType, ResultElementSourceType, ResultSetElementType, ScenarioNodeType, SimulationRunState, TableElementSourceType, } from "../rs-workspace.types";
import { filterSpaceViewerDataByConfidenceAndDensity, getGraphElementSourceFormValues, getGraphElementSourceError, getResultElementSourceTypeLabel, getResultElementsUsingInsight, getResultElementsUsingOptimization, getResultElementsUsingSimulation, getSpaceviewerSourceName, isInsightRelatedToProcessData, isScenarioItemParameterUsedAsColumn, mapSpaceViewerDataToSourceTemplate, prepareLineGraphElementExportedData, prepareProcessDataViewerElementExportedData, prepareSpaceviewerSource, prepareTabularElementExportedData, sortRowsAndColumns, prepareSpaceviewerPoints, getSimulationTimestampOptions, getTimingSummaryText, getLineGraphSourceUnitOption, getResultElementSourceComputedName, } from "../utils/results.utils";
import { getWorkspaceState, getWorkspaceStateSelector, incompleteSimulationsIdsSelector, persistedInsightsDataSelector, persistedSimulationPreviewsSelector, workspaceCellModelsSelector, workspaceCurrentInsightIdSelector, workspaceCurrentOptimizationIdSelector, workspaceCurrentSimulationIdSelector, workspaceInsightsSelector, workspaceNotRefinedCellModelsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "./rs-workspace.selectors";
import { ElementFormField, LineGraphElementFormField, SpaceviewerElementFormField, TableElementFormField, } from "pages/RS-Workspace/components/Results/components/ElementDialog";
import { defaultParameter, defaultSource, } from "pages/RS-Workspace/components/Results/components/Elements/components/SpaceViewerElement";
import { GET_ALL_CUSTOM_VARIABLES, getInsightSourceName, getRecipesCustomVariables, getSimulationSourceName, } from "../utils/scenario.utils";
import { prepareCalculationElements } from "../utils/calculations.utils";
import { sortBy } from "lodash";
import { GlobalEntityType, RsWorkspaceEntityType, SpaceviewerAxisType, TimeUnits, } from "shared/types";
import { INSIGHT_LABEL } from "rs-components/Spaceviewer/utils";
import { getAlternativeStepFlowsList, getSelectedVariable } from "store/recipe/recipe.utils";
import { getCombinationParameterPreview, getResultElementInitialName, getResultElementParameterValues, MIN_FLOWS_QUANTITY, } from "../utils/common.utils";
import { isLineGraphSourceCompleted, isProcessDataViewerSourceCompleted, isProcessDataViewerSourceSourceCompleted, isSpaceviewerSourceCompleted, isSpaceviewerSourceSourceCompleted, isSimulationVariantCompleted, isParameterDimensionCompleted, isOptimizationCompleted, isResultElementCompleted, getCombinationParameterCompletenessMessage, } from "../utils/completeness.utils";
import { getCurrentDateTimeStamp } from "utils/dateTime";
import { knowledgeBaseItemNameSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
export const getCalculationSelector = createSelector(getWorkspaceStateSelector, (state) => state.calculation);
export const isResultFetchingSelector = createSelector(getWorkspaceState, (state) => state.resultIsFetching);
export const activeResultElementSelector = createSelector(getWorkspaceState, (state) => state.activeResultElement || null);
export const duplicateTargetResultSetIdSelector = createSelector(getWorkspaceState, (state) => state.duplicateTargetResultSetId);
export const shouldScrollToRsResultElementSelector = createSelector(getWorkspaceState, (state) => state.shouldScrollToResultElement);
export const getResultSetElementByIdSelector = (resultSetId, elementId) => createSelector(getResultSetsSelector, (resultSets) => resultSets.find(({ id }) => resultSetId === id)?.elements.find(({ id }) => elementId === id));
export const getActiveResultSetIdSelector = createSelector(getWorkspaceState, (state) => state.activeResultSetId);
export const resultsRecalculationDialogOpenSelector = createSelector(getWorkspaceState, (state) => state.resultsRecalculationDialogOpen);
export const getResultSetsSelector = createSelector(getWorkspaceStateSelector, (state) => state.resultSets || []);
export const calculationTabularResultsSelector = createSelector(getWorkspaceState, (state) => state.calculationTabularResults);
export const calculationSpaceviewerResultsSelector = createSelector(getWorkspaceState, (state) => state.calculationSpaceviewerResults);
export const calculationProcessDataViewerResultsSelector = createSelector(getWorkspaceState, (state) => state.calculationProcessDataViewerResults);
export const calculationLineGraphResultsSelector = createSelector(getWorkspaceState, (state) => state.calculationLineGraphResults);
const numberOfResultsSimulationUsedInSelector = createSelector(getResultSetsSelector, workspaceInsightsSelector, workspaceCurrentSimulationIdSelector, (resultSets, insights, currentSimulationId) => resultSets.reduce((acc, resultSet) => {
    const elementsUsingSimulation = getResultElementsUsingSimulation(resultSet, insights, currentSimulationId);
    return acc + elementsUsingSimulation.length;
}, 0));
const numberOfResultsInsightUsedInSelector = createSelector(getResultSetsSelector, workspaceCurrentInsightIdSelector, (resultSets, currentInsightId) => resultSets.reduce((acc, resultSet) => {
    const elementsUsingInsight = getResultElementsUsingInsight(resultSet, currentInsightId);
    return acc + elementsUsingInsight.length;
}, 0));
const numberOfResultsOptimizationUsedInSelector = createSelector(getResultSetsSelector, workspaceCurrentOptimizationIdSelector, (resultSets, currentOptimizationId) => resultSets.reduce((acc, resultSet) => {
    const elementsUsingOptimization = getResultElementsUsingOptimization(resultSet, currentOptimizationId);
    return acc + elementsUsingOptimization.length;
}, 0));
const reFetchedResultsSimulationUsedInSelector = (currentSimulationId) => createSelector(getResultSetsSelector, workspaceInsightsSelector, (resultSets, insights) => resultSets.reduce((acc, resultSet) => {
    const elementsUsingSimulation = getResultElementsUsingSimulation(resultSet, insights, currentSimulationId);
    return [
        ...acc,
        ...elementsUsingSimulation.map((element) => ({
            resultSetId: resultSet.id,
            elementId: element.id,
        })),
    ];
}, []));
const reFetchedResultsInsightUsedInSelector = (currentInsightId) => createSelector(getResultSetsSelector, (resultSets) => resultSets.reduce((acc, resultSet) => {
    const elementsUsingInsight = getResultElementsUsingInsight(resultSet, currentInsightId);
    return [
        ...acc,
        ...elementsUsingInsight.map((element) => ({
            resultSetId: resultSet.id,
            elementId: element.id,
        })),
    ];
}, []));
const reFetchedResultsOptimizationUsedInSelector = (currentOptimizationId) => createSelector(getResultSetsSelector, (resultSets) => resultSets.reduce((acc, resultSet) => {
    const elementsUsingOptimization = getResultElementsUsingOptimization(resultSet, currentOptimizationId);
    return [
        ...acc,
        ...elementsUsingOptimization.map((element) => ({
            resultSetId: resultSet.id,
            elementId: element.id,
        })),
    ];
}, []));
export const numberOfResultsScenarioEntityUsedInSelector = (entityType) => createSelector(numberOfResultsSimulationUsedInSelector, numberOfResultsInsightUsedInSelector, numberOfResultsOptimizationUsedInSelector, (simulationUsedCount, insightUsedCount, optimizationUsedCount) => {
    if (entityType === RsWorkspaceEntityType.Simulation) {
        return simulationUsedCount;
    }
    if (entityType === RsWorkspaceEntityType.Insight) {
        return insightUsedCount;
    }
    if (entityType === RsWorkspaceEntityType.Optimization) {
        return optimizationUsedCount;
    }
    return 0;
});
export const reFetchedResultsScenarioEntityUsedInSelector = (entityType, entityId) => createSelector(reFetchedResultsSimulationUsedInSelector(entityId), reFetchedResultsInsightUsedInSelector(entityId), reFetchedResultsOptimizationUsedInSelector(entityId), (simulationResults, insightResults, optimizationResults) => {
    switch (entityType) {
        case RsWorkspaceEntityType.Simulation:
            return simulationResults;
        case RsWorkspaceEntityType.Insight:
            return insightResults;
        case RsWorkspaceEntityType.Optimization:
            return optimizationResults;
        default:
            return [];
    }
});
export const processDataRelatedResultElementIdsSelector = (processDataId) => createSelector(getResultSetsSelector, workspaceOptimizationsSelector, workspaceInsightsSelector, (resultSets, optimizations, insights) => {
    const results = [];
    for (const resultSet of resultSets) {
        for (const element of resultSet.elements) {
            switch (element.type) {
                case ResultSetElementType.Table:
                    {
                        const relatedResults = element.rows?.filter((el) => {
                            if (el.sourceType === TableElementSourceType.PROCESS_DATA) {
                                return el.sourceId === processDataId;
                            }
                            if (isNotEmpty(el.optimizationId)) {
                                const optimization = optimizations.find(({ id }) => el.optimizationId === id);
                                return isInsightRelatedToProcessData(processDataId, optimization?.state.input?.insightId, optimization?.state.input?.insightInputSetId, insights);
                            }
                            return false;
                        });
                        results.push(...relatedResults);
                    }
                    break;
                case ResultSetElementType.ProcessDataViewer:
                case ResultSetElementType.LineGraph:
                case ResultSetElementType.SpaceViewer:
                    {
                        const relatedResults = (element?.sources || []).filter((el) => {
                            if (el.source?.type === ResultElementSourceType.ProcessData) {
                                return (el.source?.processDataId ===
                                    processDataId);
                            }
                            if (el.source?.type === ResultElementSourceType.Insight) {
                                const { insightId, inputSetId } = el.source;
                                return isInsightRelatedToProcessData(processDataId, insightId, inputSetId, insights);
                            }
                            if (el.source?.type === ResultElementSourceType.Simulation) {
                                if (isNotEmpty(el.source?.optimizationId)) {
                                    const optimization = optimizations.find(({ id }) => el.source?.optimizationId === id);
                                    return isInsightRelatedToProcessData(processDataId, optimization?.state.input?.insightId, optimization?.state.input?.insightInputSetId, insights);
                                }
                            }
                            return false;
                        }) ?? [];
                        results.push(...relatedResults);
                    }
                    break;
            }
        }
    }
    return results.reduce((acc, { id }) => (isNotEmpty(id) ? [...acc, id] : acc), []);
});
export const hasSimulationRelatedResultsSelector = (simulationId) => createSelector(getResultSetsSelector, (resultSets) => {
    for (const resultSet of resultSets) {
        for (const element of resultSet.elements) {
            switch (element.type) {
                case ResultSetElementType.Table: {
                    const relatedResults = element.rows?.filter((el) => el.sourceId === simulationId && el.sourceType === TableElementSourceType.SIMULATION);
                    if (relatedResults.length) {
                        return true;
                    }
                    break;
                }
                case ResultSetElementType.SpaceViewer: {
                    const relatedSpaceViewerResults = element.sources?.filter((el) => el.source?.type === ResultElementSourceType.Simulation &&
                        el.source.id === simulationId);
                    if (relatedSpaceViewerResults.length) {
                        return true;
                    }
                    break;
                }
                case ResultSetElementType.ProcessDataViewer: {
                    const relatedProcessDataViewerResults = element.sources?.filter((el) => el.source?.type === ResultElementSourceType.Simulation &&
                        el.source?.simulationId === simulationId);
                    if (relatedProcessDataViewerResults?.length) {
                        return true;
                    }
                    break;
                }
                case ResultSetElementType.LineGraph: {
                    const relatedLineGraphResults = element.sources?.filter((el) => el.source?.type === ResultElementSourceType.Simulation &&
                        el.source?.simulationId === simulationId);
                    if (relatedLineGraphResults?.length) {
                        return true;
                    }
                    break;
                }
            }
        }
    }
    return false;
});
export const isOptimizationUsedInResultsSelector = (id) => createSelector(getResultSetsSelector, (resultSets) => {
    for (const resultSet of resultSets) {
        for (const element of resultSet.elements) {
            if (element.type === ResultSetElementType.Table) {
                const relatedResults = element.rows.some(({ optimizationId }) => optimizationId === id);
                if (relatedResults) {
                    return true;
                }
            }
        }
    }
    return false;
});
export const isResultElementUsedInCalculationsSelector = (element, shouldBeInProgress = true) => createSelector(activeResultElementSelector, getCalculationSelector, (activeResultElement, calculations) => {
    const resultElement = element || activeResultElement;
    if (!resultElement) {
        return false;
    }
    const relatedCalculation = calculations.elements.find((item) => item.resultSetId === resultElement.resultSetId && item.resultElementId === resultElement.elementId);
    if (!shouldBeInProgress) {
        return !!relatedCalculation?.rows.length;
    }
    const elementInProgress = relatedCalculation?.rows.filter((item) => item.status === CalculationStatusSource.InProgress || CalculationStatusSource.Paused);
    return !!elementInProgress?.length;
});
export const hasFetchingResultsSelector = (resultSetId, elementId, elementType) => createSelector(calculationElementSelector(resultSetId, elementId), calculationTabularResultsSelector, calculationSpaceviewerResultsSelector, calculationProcessDataViewerResultsSelector, calculationLineGraphResultsSelector, (calculationElement, calculationTabularResults, calculationSpaceviewerResults, calculationProcessDataViewerResults, calculationLineGraphResults) => {
    const hasCalculatingSource = calculationElement?.rows?.some(({ status }) => status === CalculationStatusSource.InProgress);
    switch (elementType) {
        case ResultSetElementType.Table: {
            const hasFetchingRow = calculationTabularResults
                .find((item) => compareResultItemIds(item, {
                resultSetId,
                resultSetElementId: elementId,
            }))
                ?.rows.some(({ fetching }) => !!fetching);
            return hasCalculatingSource || hasFetchingRow;
        }
        case ResultSetElementType.SpaceViewer: {
            return (hasCalculatingSource ||
                !!calculationSpaceviewerResults
                    .find((item) => compareResultItemIds(item, {
                    resultSetId,
                    resultSetElementId: elementId,
                }))
                    ?.sources.some(({ fetching }) => !!fetching));
        }
        case ResultSetElementType.ProcessDataViewer: {
            const hasFetchingSource = calculationProcessDataViewerResults
                .find((item) => compareResultItemIds(item, {
                resultSetId,
                resultSetElementId: elementId,
            }))
                ?.results.some(({ fetching }) => !!fetching);
            return hasCalculatingSource || hasFetchingSource;
        }
        case ResultSetElementType.LineGraph: {
            const fetchingSource = calculationLineGraphResults
                .find((item) => compareResultItemIds(item, {
                resultSetId,
                resultSetElementId: elementId,
            }))
                ?.results.some(({ fetching }) => !!fetching);
            return hasCalculatingSource || fetchingSource;
        }
    }
});
export const resultElementParameterValuesSelector = (parameter, sources) => createSelector(workspaceNotRefinedCellModelsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, workspaceInsightsSelector, (noRefinedCellModels, recipes, simulations, insights) => getResultElementParameterValues(parameter, sources, noRefinedCellModels, recipes, simulations, insights));
export const expandedActiveResultElementSelector = createSelector(activeResultElementSelector, getResultSetsSelector, workspaceRecipesSelector, workspaceCellModelsSelector, parameterTypesSelector, processVariablesOptionsSelector, workspaceSimulationsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, workspaceInsightsSelector, persistedInsightsDataSelector, processVariablesSelector, persistedInsightsDataSelector, incompleteSimulationsIdsSelector, duplicateTargetResultSetIdSelector, (activeResultElement, resultSets, recipes, cellModels, parameterTypes, variableOptionsList, simulations, optimizations, processData, insights, persistedInsights, processVariables, persistedInsightsData, incompleteSimulationsIds, duplicateTargetResultSetId) => {
    if (activeResultElement === null) {
        return {};
    }
    const { index, resultSetId, type, elementId } = activeResultElement;
    const elements = resultSets.find(({ id }) => resultSetId === id)?.elements || [];
    const element = elements.find((element) => element.type === type && element.id === elementId) ||
        { id: -1, type };
    const initialValues = {
        [ElementFormField.name]: getResultElementInitialName(element.name, elements, duplicateTargetResultSetId),
    };
    switch (type) {
        case ResultSetElementType.Table: {
            const { rows = [], columns = [] } = sortRowsAndColumns(element);
            initialValues[TableElementFormField.rows] = rows;
            initialValues[TableElementFormField.columns] = columns;
            break;
        }
        case ResultSetElementType.SpaceViewer: {
            const { parameters = [], sources = [] } = element;
            initialValues[SpaceviewerElementFormField.parameters] =
                element.id !== -1 ? parameters : [{ ...defaultParameter, id: 1 }];
            initialValues[SpaceviewerElementFormField.sources] =
                element.id !== -1
                    ? sources.map((source) => prepareSpaceviewerSource(source, variableOptionsList, parameterTypes, simulations, insights, persistedInsights, processVariables, recipes))
                    : [{ ...defaultSource, view: true, id: 1 }];
            break;
        }
        case ResultSetElementType.ProcessDataViewer: {
            const processDataViewerSources = element?.sources ?? [];
            initialValues.sources =
                processDataViewerSources?.length > 0
                    ? processDataViewerSources?.map((source) => getGraphElementSourceFormValues(source, null, cellModels, recipes, simulations, incompleteSimulationsIds, insights, persistedInsightsData, optimizations, processData, variableOptionsList, parameterTypes, processVariables).preparedSource)
                    : [{ ...processDataViewerDefaultSource, id: 1 }];
            break;
        }
        case ResultSetElementType.LineGraph: {
            const lineGraphSources = element?.sources ?? [];
            const lineGraphParameter = element?.parameter;
            initialValues[LineGraphElementFormField.parameter] =
                element?.id !== -1
                    ? lineGraphParameter
                    : { ...defaultParameter, id: 1 };
            initialValues[LineGraphElementFormField.sources] =
                lineGraphSources?.length > 0
                    ? lineGraphSources?.map((source) => getGraphElementSourceFormValues(source, lineGraphParameter, cellModels, recipes, simulations, incompleteSimulationsIds, insights, persistedInsightsData, optimizations, processData, variableOptionsList, parameterTypes, processVariables).preparedSource)
                    : [{ ...lineGraphDefaultSource, id: 1 }];
            break;
        }
    }
    return {
        index,
        element,
        initialValues,
    };
});
export const hasResultsRelatedToInsight = (insightId) => createSelector(getResultSetsSelector, (resultSets) => {
    for (const resultSet of resultSets) {
        for (const element of resultSet.elements) {
            const relatedResults = element.rows?.some((el) => el.sourceId === insightId);
            const relatedSpaceViewerResults = element.sources?.some((item) => {
                const src = item?.source;
                src?.type === ResultElementSourceType.Insight && src?.insightId === insightId;
            });
            if (relatedResults || relatedSpaceViewerResults) {
                return true;
            }
        }
    }
    return false;
});
export const calculationProgressStatusSelector = createSelector(getWorkspaceStateSelector, (state) => state.calculationStatus || CalculationStatusSource.New);
export const calculationStatusIsChangingSelector = createSelector(getWorkspaceStateSelector, (state) => state.calculationStatusIsChanging || false);
export const calculationElementSelector = (resultSetId, elementId) => createSelector(getCalculationSelector, (calculation) => calculation.elements.find((element) => element.resultSetId === resultSetId && element.resultElementId === elementId) || {});
export const calculationElementRowSelector = (payload, responseId) => createSelector(calculationElementSelector(payload.resultSetId, payload.resultSetElementId), (calculationElement) => calculationElement?.rows?.find((row) => responseId === row.responseId) ||
    {});
export const resultElementsForCalculationsSelector = createSelector(getResultSetsSelector, workspaceSimulationsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, workspaceInsightsSelector, processVariablesSelector, workspaceCellModelsSelector, workspaceRecipesSelector, persistedInsightsDataSelector, incompleteSimulationsIdsSelector, processVariablesOptionsSelector, parameterTypesSelector, (resultSets, simulations, optimizations, processDatas, insights, processVariables, cellModels, recipes, persistedInsightsData, incompleteSimulationsIds, variableOptionsList, parameterTypes) => resultSets.reduce((preparedResultSets, resultSet) => {
    const elements = sortBy(resultSet.elements, ["row", "column"]).reduce((preparedElements, element) => {
        const { id, name = "", type } = element;
        const isElementComplete = isResultElementCompleted(element, cellModels, recipes, simulations, insights, processVariables, incompleteSimulationsIds, optimizations, true);
        const customVariables = getRecipesCustomVariables(recipes, GET_ALL_CUSTOM_VARIABLES);
        switch (type) {
            case ResultSetElementType.Table: {
                const preparedSources = element.rows.reduce((acc, row) => {
                    const { sourceId, sourceType, optimizationId, adjustmentId } = row;
                    switch (sourceType) {
                        case TableElementSourceType.PROCESS_DATA:
                            return [
                                ...acc,
                                {
                                    id: row.id || 0,
                                    sourceType: CalculationElementSourceType.ProcessData,
                                    sourceName: processDatas.find(({ id }) => sourceId === id)?.name ?? "",
                                    sourceId,
                                    isCompleted: true,
                                },
                            ];
                        case TableElementSourceType.SIMULATION: {
                            const simulation = simulations?.find(({ id }) => id === sourceId);
                            const variant = simulation?.state?.adjustments?.find(({ id }) => id === adjustmentId);
                            let isCompleted = !(sourceId && incompleteSimulationsIds.includes(sourceId)) &&
                                isSimulationVariantCompleted(simulation, cellModels, recipes, variant);
                            const optimization = optimizations?.find(({ id }) => optimizationId === id);
                            if (optimizationId !== null) {
                                isCompleted = isOptimizationCompleted(optimization?.state, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
                            }
                            return [
                                ...acc,
                                {
                                    id: row.id || 0,
                                    sourceType: optimizationId !== null
                                        ? CalculationElementSourceType.Optimization
                                        : CalculationElementSourceType.Simulation,
                                    sourceName: getSimulationSourceName(simulations, optimizations, sourceId, optimizationId, adjustmentId),
                                    sourceId,
                                    isCompleted,
                                },
                            ];
                        }
                        default:
                            return acc;
                    }
                }, []);
                preparedElements.push({
                    id,
                    name,
                    type,
                    isCompleted: isElementComplete && preparedSources.some(({ isCompleted }) => isCompleted),
                    sources: preparedSources,
                });
                break;
            }
            case ResultSetElementType.SpaceViewer: {
                const { sources, parameters } = element;
                const preparedSources = sources
                    .filter(({ source }) => isNotEmpty(source) && isNotEmpty(id))
                    .reduce((acc, row) => {
                    const { source } = row;
                    const sourceTemplate = mapSpaceViewerDataToSourceTemplate(row.source);
                    const hasMatchedParameter = parameters.some((parameter) => getCombinationParameterCompletenessMessage(parameter, [sourceTemplate], insights, simulations, cellModels, recipes) === undefined);
                    if (!hasMatchedParameter) {
                        return acc;
                    }
                    const preparedSource = prepareSpaceviewerSource(row, variableOptionsList, parameterTypes, simulations, insights, persistedInsightsData, processVariables, recipes);
                    const isCompleted = isSpaceviewerSourceCompleted(sourceTemplate, processVariables) &&
                        isSpaceviewerSourceSourceCompleted(sourceTemplate, processVariables, simulations, insights, cellModels, recipes, incompleteSimulationsIds);
                    if (source?.type === ResultElementSourceType.Simulation) {
                        const timestampOptions = getSimulationTimestampOptions(preparedSource?.simulationId, simulations, recipes);
                        return [
                            ...acc,
                            {
                                id: row.id || 0,
                                sourceName: getSimulationSourceName(simulations, optimizations, source.id, null, source.adjustmentId),
                                sourceType: CalculationElementSourceType.Simulation,
                                sourceId: source.id ?? null,
                                sourceVariable: preparedSource?._variableName?.label,
                                sourceUnit: preparedSource?.units?.label,
                                sourceTiming: getTimingSummaryText(preparedSource.timing || null, timestampOptions, processVariables, customVariables),
                                isCompleted,
                            },
                        ];
                    }
                    else {
                        const { insightId } = source;
                        return [
                            ...acc,
                            {
                                id: row.id || 0,
                                sourceName: getSpaceviewerSourceName({ source }, simulations, insights, persistedInsightsData, processVariables, recipes),
                                sourceType: CalculationElementSourceType.Insight,
                                sourceId: insightId ?? 0,
                                isCompleted,
                            },
                        ];
                    }
                }, []);
                const hasCompletedParameter = preparedSources.length > 0;
                preparedElements.push({
                    id,
                    name,
                    type,
                    isCompleted: isElementComplete &&
                        hasCompletedParameter &&
                        preparedSources.some(({ isCompleted }) => isCompleted),
                    sources: preparedSources,
                });
                break;
            }
            case ResultSetElementType.ProcessDataViewer: {
                const preparedSources = (element?.sources ?? [])
                    .filter(({ source }) => isNotEmpty(source) && isNotEmpty(id))
                    .reduce((acc, row) => {
                    const { source } = row;
                    const isCompleted = isProcessDataViewerSourceCompleted(row) &&
                        isProcessDataViewerSourceSourceCompleted(row, insights, optimizations, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
                    switch (source?.type) {
                        case ResultElementSourceType.Simulation: {
                            const { simulationId, optimizationId, adjustmentId, customVariableId, variableType, timing, } = {
                                ...source,
                            };
                            const timestampOptions = getSimulationTimestampOptions(simulationId, simulations, recipes);
                            const customVariableEntity = customVariables.find((item) => item.id === customVariableId);
                            const processVariableName = variableOptionsList.find((item) => item.value === variableType)?.label ?? "";
                            return [
                                ...acc,
                                {
                                    id: row.id || 0,
                                    sourceName: getSimulationSourceName(simulations, optimizations, simulationId, optimizationId, adjustmentId),
                                    sourceType: optimizationId !== null
                                        ? CalculationElementSourceType.Optimization
                                        : CalculationElementSourceType.Simulation,
                                    sourceVariable: customVariableId
                                        ? (customVariableEntity?.name ?? "")
                                        : processVariableName,
                                    sourceUnit: getLineGraphSourceUnitOption(source, simulations, recipes, parameterTypes)?.label,
                                    sourceTiming: getTimingSummaryText(timing || null, timestampOptions, processVariables, customVariables),
                                    sourceId: optimizationId ?? simulationId ?? 0,
                                    isCompleted,
                                },
                            ];
                        }
                        case ResultElementSourceType.Insight: {
                            const { insightId, inputSetId, output } = {
                                ...source,
                            };
                            return [
                                ...acc,
                                {
                                    id: row.id || 0,
                                    sourceName: getInsightSourceName(insights, persistedInsightsData, processVariables, insightId, inputSetId, output, recipes),
                                    sourceType: CalculationElementSourceType.Insight,
                                    sourceId: insightId ?? 0,
                                    isCompleted,
                                },
                            ];
                        }
                        case ResultElementSourceType.ProcessData: {
                            const { processDataId, variableType, liquid } = {
                                ...source,
                            };
                            const processVariableName = variableOptionsList.find((item) => item.value === variableType)?.label ?? "";
                            return [
                                ...acc,
                                {
                                    id: row.id || 0,
                                    sourceName: processDatas?.find(({ id }) => id === processDataId)?.name || "",
                                    sourceType: CalculationElementSourceType.ProcessData,
                                    sourceVariable: liquid ? liquid : processVariableName,
                                    sourceUnit: getLineGraphSourceUnitOption(source, simulations, recipes, parameterTypes)?.label,
                                    sourceId: processDataId ?? 0,
                                    isCompleted,
                                },
                            ];
                        }
                        default:
                            return acc;
                    }
                }, []);
                preparedElements.push({
                    id,
                    name,
                    type,
                    sources: preparedSources,
                    isCompleted: isElementComplete && preparedSources.some(({ isCompleted }) => isCompleted),
                });
                break;
            }
            case ResultSetElementType.LineGraph: {
                const { sources, parameter } = { ...element };
                const preparedSources = (sources ?? [])
                    .filter(({ source }) => isNotEmpty(source) && isNotEmpty(id))
                    .reduce((acc, row) => {
                    const { source } = row;
                    const isCompleted = isLineGraphSourceCompleted(row, processVariables) &&
                        isProcessDataViewerSourceSourceCompleted(row, insights, optimizations, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
                    const parameterCompletenessMessage = getCombinationParameterCompletenessMessage(parameter, [mapSpaceViewerDataToSourceTemplate(row)], insights, simulations, cellModels, recipes);
                    if (parameterCompletenessMessage) {
                        return acc;
                    }
                    if (source?.type === ResultElementSourceType.Simulation) {
                        const { simulationId, adjustmentId, optimizationId, customVariableId, variableType, timing, } = {
                            ...source,
                        };
                        const timestampOptions = getSimulationTimestampOptions(simulationId, simulations, recipes);
                        const customVariableEntity = customVariables.find((item) => item.id === customVariableId);
                        const processVariableName = variableOptionsList.find((item) => item.value === variableType)?.label ?? "";
                        return [
                            ...acc,
                            {
                                id: row.id || 0,
                                sourceName: getSimulationSourceName(simulations, optimizations, simulationId, optimizationId, adjustmentId),
                                sourceVariable: customVariableId
                                    ? (customVariableEntity?.name ?? "")
                                    : processVariableName,
                                sourceUnit: getLineGraphSourceUnitOption(source, simulations, recipes, parameterTypes)
                                    ?.label,
                                sourceTiming: getTimingSummaryText(timing || null, timestampOptions, processVariables, customVariables),
                                sourceType: optimizationId !== null
                                    ? CalculationElementSourceType.Optimization
                                    : CalculationElementSourceType.Simulation,
                                sourceId: optimizationId ?? simulationId ?? 0,
                                isCompleted,
                            },
                        ];
                    }
                    if (source?.type === ResultElementSourceType.Insight) {
                        const { insightId, inputSetId, output } = {
                            ...source,
                        };
                        return [
                            ...acc,
                            {
                                id: row.id || 0,
                                sourceName: getInsightSourceName(insights, persistedInsightsData, processVariables, insightId, inputSetId, output, recipes),
                                sourceType: CalculationElementSourceType.Insight,
                                sourceId: insightId ?? 0,
                                isCompleted,
                            },
                        ];
                    }
                    return acc;
                }, []);
                const hasMatchingParameter = preparedSources.length > 0;
                preparedElements.push({
                    id,
                    name,
                    type,
                    sources: preparedSources,
                    isCompleted: isElementComplete &&
                        hasMatchingParameter &&
                        preparedSources.some(({ isCompleted }) => isCompleted),
                });
                break;
            }
        }
        return preparedElements;
    }, []);
    if (elements.length > 0) {
        const { id, name } = resultSet;
        preparedResultSets.push({ id, name, elements });
    }
    return preparedResultSets;
}, []));
export const calculationDataSelector = createSelector(calculationProgressStatusSelector, getCalculationSelector, resultElementsForCalculationsSelector, (calculationStatus, calculation, preparedElements) => ({
    calculationStatus,
    calculation,
    preparedElements,
    availableElements: preparedElements.reduce((acc, resultSet) => {
        const newSet = {
            ...resultSet,
            elements: resultSet.elements.reduce((elementsAcc, element) => {
                const newElement = {
                    ...element,
                    sources: element.sources.filter(({ isCompleted }) => isCompleted),
                };
                if (newElement.sources.length > 0 && newElement.isCompleted) {
                    elementsAcc.push(newElement);
                }
                return elementsAcc;
            }, []),
        };
        if (newSet.elements.length > 0) {
            acc.push(newSet);
        }
        return acc;
    }, []),
    queuedElements: prepareCalculationElements(calculation, preparedElements),
}));
export const getResultsError = ({ resultSetId, resultSetElementId, rowId, }) => createSelector(getCalculationSelector, (calculation) => calculation.elements
    .find((item) => compareResultItemIds({ resultSetElementId: item.resultElementId, resultSetId: item.resultSetId }, { resultSetElementId, resultSetId }))
    ?.rows?.find((row) => row.id === rowId)?.error);
export const getActiveResultSetSelector = createSelector(getActiveResultSetIdSelector, getResultSetsSelector, (activeResultSetId, resultSets) => {
    return resultSets.find(({ id }) => id === activeResultSetId);
});
export const resultSetByIdSelector = (resultSetId) => createSelector(getResultSetsSelector, (resultSets) => resultSets.find(({ id }) => id === resultSetId));
export const isActiveResultSetDeletable = createSelector(getResultSetsSelector, (resultSets) => resultSets.length > 1);
export const isActiveResultSetUsedInCalculationsSelector = createSelector(getActiveResultSetIdSelector, getCalculationSelector, (activeResultSetId, calculations) => calculations.elements.some(({ resultSetId }) => resultSetId === activeResultSetId));
export const getActiveResultSetElements = createSelector(getActiveResultSetSelector, (resultSet) => resultSet?.elements ?? []);
export const getCalculationTableResultSelector = (payload) => createSelector(calculationTabularResultsSelector, calculationElementSelector(payload.resultSetId, payload.resultSetElementId), (calculationTabularResults, calculationElement) => {
    const results = calculationTabularResults.find((item) => compareResultItemIds(item, payload));
    return { resultRows: results?.rows, calculationRows: calculationElement.rows };
});
export const getAlternativeStepParameterInitialValues = (parameter) => createSelector(workspaceRecipesSelector, (recipes) => {
    if (parameter?.type !== ResultElementParameterType.ALTERNATIVE_FLOW) {
        return undefined;
    }
    const flowsList = getAlternativeStepFlowsList(recipes, parameter?.name);
    const flowIndexesSelection = flowsList.map((_, index) => !!parameter?.flowIndexes?.includes(index));
    return { flowsList, flowIndexesSelection, flowIndexes: parameter.flowIndexes };
});
export const getCalculationSpaceviewerResultSelector = (payload) => createSelector(getResultSetElementByIdSelector(payload.resultSetId, payload.resultSetElementId), calculationSpaceviewerResultsSelector, workspaceSimulationsSelector, persistedSimulationPreviewsSelector, processVariablesSelector, workspaceCellModelsSelector, workspaceRecipesSelector, workspaceInsightsSelector, parameterTypesSelector, incompleteSimulationsIdsSelector, (element, calculationSpaceviewerResults, simulations, persistedSimulationPreviews, processVariables, cellModels, recipes, insights, parameterTypes, incompleteSimulationsIds) => {
    const { sources, parameters: elementParameters, xAxisId, yAxisId, filteredParameters, } = (element ?? {});
    const source = sources?.find(({ view }) => view) || {};
    const result = calculationSpaceviewerResults
        .find((item) => compareResultItemIds(item, payload))
        ?.sources.find(({ sourceId }) => sourceId === source.id);
    const actualFilteredParameters = filteredParameters?.find(({ spaceViewElementSourceId }) => spaceViewElementSourceId === source.id);
    const parameters = elementParameters
        ?.reduce((acc, parameter) => {
        switch (parameter.parameter?.type) {
            case ResultElementParameterType.CELL_MODEL:
            case ResultElementParameterType.RECIPE: {
                const { min, max, type } = { ...parameter?.parameter };
                if ([parameter, parameter?.id, min, max].every((item) => isNotEmpty(item))) {
                    const sourceType = type === ResultElementParameterType.CELL_MODEL
                        ? GlobalEntityType.CellModel
                        : GlobalEntityType.Recipe;
                    const { name, units } = getCombinationParameterPreview(parameter, recipes, cellModels, parameterTypes).parameter;
                    const axis = {
                        id: parameter?.id,
                        sourceType,
                        min: min,
                        max: max,
                        name: name ?? "",
                        originalName: parameter?.parameter?.name,
                        unit: units,
                        type: SpaceviewerAxisType.FLOAT,
                    };
                    acc.push(axis);
                }
                break;
            }
            case ResultElementParameterType.ALTERNATIVE_FLOW: {
                const { flowIndexes, name } = { ...parameter.parameter };
                if ((flowIndexes?.length ?? 0) >= MIN_FLOWS_QUANTITY) {
                    const flowsList = getAlternativeStepFlowsList(recipes, name);
                    const sortedFlowIndexes = [...(flowIndexes ?? [])].sort((a, b) => a - b);
                    const steps = sortedFlowIndexes.map((flowIndex, idx) => ({
                        label: flowsList[flowIndex] ?? "",
                        value: idx,
                    }));
                    const axis = {
                        id: parameter?.id,
                        sourceType: GlobalEntityType.Recipe,
                        name: name ?? "",
                        originalName: name ?? "",
                        type: SpaceviewerAxisType.INTEGER,
                        steps,
                        flowIds: sortedFlowIndexes,
                    };
                    acc.push(axis);
                }
                break;
            }
            default:
                break;
        }
        return acc;
    }, [])
        .filter(({ id }) => isNotEmpty(actualFilteredParameters)
        ? actualFilteredParameters?.combinationElementParameterIds.includes(Number(id))
        : true) || [];
    const selectedXIndex = parameters.findIndex(({ id }) => xAxisId === id);
    const selectedYIndex = parameters.findIndex(({ id }) => yAxisId === id);
    const selectedAxisIndexes = [
        selectedXIndex === -1 ? undefined : selectedXIndex,
        selectedYIndex === -1 ? undefined : selectedYIndex,
    ];
    const sourceCompleted = isSpaceviewerSourceSourceCompleted(mapSpaceViewerDataToSourceTemplate(source), processVariables, simulations, insights, cellModels, recipes, incompleteSimulationsIds);
    let sourceVariable;
    let sourceLabel;
    let isInsight = false;
    let errorMessage = "";
    switch (source.source?.type) {
        case ResultElementSourceType.Simulation: {
            const simulationSource = source.source;
            const usedSimulation = simulations.find(({ id }) => simulationSource.id === id);
            const combinedId = `${usedSimulation?.id}-${0}`;
            const customVariableOptions = getRecipesCustomVariables(recipes, [usedSimulation?.recipeId], ({ recordInData }) => Boolean(recordInData));
            sourceVariable =
                getSelectedVariable(customVariableOptions, processVariables, simulationSource?.customVariableId ?? undefined, simulationSource?.variableType ?? undefined)?.name ?? "Source parameter is not selected";
            sourceLabel =
                (simulationSource.variableType || simulationSource?.customVariableId) && usedSimulation?.name
                    ? `${getSimulationSourceName(simulations, [], simulationSource.id, null, simulationSource.adjustmentId)} - ${sourceVariable}`
                    : "Source parameter is not selected";
            if (!!persistedSimulationPreviews?.[combinedId]?.graphData?.errorMessage || !sourceCompleted) {
                errorMessage = "Simulation is no longer valid.";
            }
            break;
        }
        case ResultElementSourceType.Insight:
            sourceVariable = INSIGHT_LABEL;
            sourceLabel = INSIGHT_LABEL;
            isInsight = true;
            if (!sourceCompleted) {
                errorMessage = "Insight is no longer valid.";
            }
            break;
    }
    const points = prepareSpaceviewerPoints(parameters, result?.points);
    return {
        data: {
            values: sourceCompleted ? filterSpaceViewerDataByConfidenceAndDensity(points, source) : [],
            parameters: (result?.parameterIdsSequence?.length || 0) > 0
                ? result?.parameterIdsSequence.map((parameterId) => (parameters.find(({ id }) => parameterId === id) || {}))
                : parameters,
            source,
            sourceCompleted,
            value: 0,
            maxPointsNumber: points?.length,
        },
        sourceVariable,
        sourceLabel,
        isInsight,
        error: errorMessage || (result?.state === SimulationRunState.ERROR ? result?.error : ""),
        selectedAxisIndexes: selectedAxisIndexes.every((index) => index === undefined)
            ? null
            : selectedAxisIndexes,
    };
});
export const calculationSpaceviewerSourceStatusSelector = (resultSetId, elementId, spaceviewerSource, parameters) => createSelector(calculationSpaceviewerResultsSelector, calculationElementSelector(resultSetId, elementId), incompleteSimulationsIdsSelector, workspaceInsightsSelector, processVariablesSelector, workspaceCellModelsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, (calculationSpaceviewerResults, calculationElement, incompleteSimulationsIds, insights, processVariables, cellModels, recipes, simulations) => {
    const { id, source } = spaceviewerSource;
    const result = calculationSpaceviewerResults
        .find((item) => compareResultItemIds(item, { resultSetId, resultSetElementId: elementId }))
        ?.sources.find(({ sourceId }) => sourceId === id);
    const { status } = (calculationElement.rows?.find(({ responseId }) => responseId === id) ||
        {});
    const hasSetParameter = parameters.some((parameter) => {
        const isFilled = isParameterDimensionCompleted(parameter);
        const completenessMessage = getCombinationParameterCompletenessMessage(parameter, [mapSpaceViewerDataToSourceTemplate(source)], insights, simulations, cellModels, recipes);
        return completenessMessage === undefined && isFilled;
    });
    const sourceTemplate = mapSpaceViewerDataToSourceTemplate(source);
    return {
        accuracy: result?.accuracy ?? null,
        calculationStatus: status,
        resultStatus: result?.state,
        fetching: !!result?.fetching,
        isCompleted: isSpaceviewerSourceSourceCompleted(sourceTemplate, processVariables, simulations, insights, cellModels, recipes, incompleteSimulationsIds),
        sourceTypeLabel: getResultElementSourceTypeLabel(source?.type),
        isFilled: isSpaceviewerSourceCompleted(sourceTemplate, processVariables),
        resultsAreAvailable: !!result?.points?.length,
        hasNoParameters: !hasSetParameter,
        error: result?.error ?? "",
    };
});
export const calculationLineGraphElementResultSelector = (payload, elementType) => createSelector(getResultSetElementByIdSelector(payload.resultSetId, payload.resultSetElementId), calculationProcessDataViewerResultsSelector, calculationLineGraphResultsSelector, processVariablesOptionsSelector, parameterTypesSelector, workspaceSimulationsSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, workspaceRecipesSelector, workspaceCellModelsSelector, persistedInsightsDataSelector, processVariablesSelector, incompleteSimulationsIdsSelector, persistedSimulationPreviewsSelector, (element, calculationProcessDataViewerResults, calculationLineGraphResults, variableOptionsList, parameterTypes, simulations, insights, optimizations, processData, recipes, cellModels, persistedInsightsData, processVariables, incompleteSimulationIds, persistedSimulationPreviews) => {
    const result = elementType === ResultSetElementType.LineGraph
        ? calculationLineGraphResults.find((item) => compareResultItemIds(item, payload))
        : calculationProcessDataViewerResults.find((item) => compareResultItemIds(item, payload));
    const customVariables = getRecipesCustomVariables(recipes, GET_ALL_CUSTOM_VARIABLES);
    return element?.sources?.map((source) => {
        const sourceResult = result?.results?.find(({ sourceId }) => sourceId === source.id);
        const { preparedSource } = getGraphElementSourceFormValues(source, element?.parameter ?? null, cellModels, recipes, simulations, incompleteSimulationIds, insights, persistedInsightsData, optimizations, processData, variableOptionsList, parameterTypes, processVariables);
        const sourceError = getGraphElementSourceError(source, persistedSimulationPreviews, persistedInsightsData, optimizations, incompleteSimulationIds, processVariables, insights, simulations, cellModels, recipes);
        const sourceLineTitle = preparedSource?.source?.type === ResultElementSourceType.Insight
            ? (preparedSource?.source?._sourceName ?? "")
            : (preparedSource?.source?._variableName?.label ?? "");
        let sourceLineUnits = preparedSource?.source?.units?.label ?? "";
        const unitsPrefix = getDefaultUnitsPrefix(preparedSource?.source?.units?.value);
        if (sourceLineUnits && unitsPrefix) {
            sourceLineUnits = `${unitsPrefix} ${sourceLineUnits}`;
        }
        const sourceLineComputedName = getResultElementSourceComputedName(source?.source, simulations, optimizations, insights, recipes, persistedInsightsData, processVariables, processData);
        const timestampOptions = getSimulationTimestampOptions(preparedSource?.source?.simulationId ?? undefined, simulations, recipes);
        const sourceLineTiming = getTimingSummaryText(preparedSource?.source?.timing || null, timestampOptions, processVariables, customVariables);
        const sourceConfig = {
            source,
            sourceError,
            sourceLineTitle,
            sourceLineUnits,
            sourceLineComputedName,
            sourceLineTiming,
        };
        if (!sourceResult) {
            return {
                sourceId: source.id ?? 0,
                fetching: false,
                state: SimulationRunState.UNKNOWN,
                accuracy: null,
                elementType,
                errors: [],
                hasErrors: false,
                ...sourceConfig,
            };
        }
        return {
            ...{
                ...sourceResult,
                elementType,
                ...(sourceResult.hasErrors
                    ? { error: sourceResult.error, errors: sourceResult.errors }
                    : { error: undefined, errors: [] }),
            },
            ...sourceConfig,
        };
    });
});
export const calculationGraphElementSourceResultSelector = (payload, elementType, id) => createSelector(calculationLineGraphElementResultSelector(payload, elementType), (calculationProcessDataViewerResult) => calculationProcessDataViewerResult?.find(({ sourceId }) => sourceId === id));
export const hasErroredSourceSelector = (payload, elementType) => createSelector(calculationLineGraphElementResultSelector(payload, elementType), (calculationProcessDataViewerResult) => calculationProcessDataViewerResult?.some(({ hasErrors }) => hasErrors) || false);
export const processDataViewerResultsTimeUnitSelector = (payload) => createSelector(calculationProcessDataViewerResultsSelector, (calculationProcessDataViewerResults) => calculationProcessDataViewerResults.find((item) => compareResultItemIds(item, payload))?.timeUnit ??
    TimeUnits.hours);
export const processDataViewerSourceFormValuesSelector = (source, parameter) => createSelector(processVariablesOptionsSelector, parameterTypesSelector, workspaceSimulationsSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, workspaceRecipesSelector, workspaceCellModelsSelector, persistedInsightsDataSelector, processVariablesSelector, incompleteSimulationsIdsSelector, (variableOptionsList, parameterTypes, simulations, insights, optimizations, processData, recipes, cellModels, persistedInsightsData, processVariables, incompleteSimulationsIds) => getGraphElementSourceFormValues(source, parameter ?? null, cellModels, recipes, simulations, incompleteSimulationsIds, insights, persistedInsightsData, optimizations, processData, variableOptionsList, parameterTypes, processVariables));
export const processDataViewerGraphPointsSelector = (payload) => createSelector(calculationProcessDataViewerResultsSelector, (results) => results.find((item) => compareResultItemIds(item, payload))?.results);
export const processDataViewerGraphErrorsPointsSelector = (payload, source) => createSelector(calculationProcessDataViewerResultsSelector, (results) => {
    const resultIndex = results.findIndex((item) => compareResultItemIds(item, payload));
    if (resultIndex < 0) {
        return;
    }
    return results[resultIndex]?.results?.find((sourceResult) => sourceResult.sourceId === source.id);
});
export const someProcessDataRelatedResultElementsInCalculationSelector = (relatedResultElementIds) => createSelector(getCalculationSelector, (calculation) => calculation.elements.some((element) => relatedResultElementIds.includes(element.resultElementId)));
export const isScenarioItemReferencedInResultsSelector = (itemId, type) => createSelector(getResultSetsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, workspaceInsightsSelector, (results, cellModels, recipes, simulations, insights) => {
    let scenarioItem;
    let simulation;
    switch (type) {
        case ScenarioNodeType.CELLMODEL:
            scenarioItem = cellModels.find(({ id }) => id === itemId) || {};
            simulation = simulations.find((simulation) => simulation.cellModelId === scenarioItem?.id);
            break;
        case ScenarioNodeType.RECIPE:
            scenarioItem = recipes.find(({ id }) => id === itemId) || {};
            simulation = simulations.find((simulation) => simulation.recipeId === scenarioItem?.id);
            break;
    }
    for (const resultSet of results) {
        if (simulation) {
            if (getResultElementsUsingSimulation(resultSet, insights, simulation.id).length > 0) {
                return true;
            }
        }
        for (const element of resultSet.elements) {
            switch (element.type) {
                case ResultSetElementType.Table:
                    if (isScenarioItemParameterUsedAsColumn(element, scenarioItem, type)) {
                        return true;
                    }
            }
        }
    }
    return false;
});
export const isResultElementFailedInCalculationSelector = (resultSetId, elementId) => createSelector(calculationElementSelector(resultSetId, elementId), (calculationElement) => calculationElement?.rows?.every(({ status }) => status === CalculationStatusSource.Error));
export const resultElementExportedDataSelector = (resultSetId, element) => createSelector(workspaceSimulationsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, processVariablesSelector, workspaceInsightsSelector, workspaceRecipesSelector, workspaceCellModelsSelector, parameterTypesSelector, processVariablesOptionsSelector, persistedInsightsDataSelector, persistedSimulationPreviewsSelector, knowledgeBaseItemNameSelector, incompleteSimulationsIdsSelector, getCalculationTableResultSelector({ resultSetElementId: element.id, resultSetId }), calculationLineGraphElementResultSelector({ resultSetElementId: element.id, resultSetId }, ResultSetElementType.LineGraph), calculationLineGraphElementResultSelector({ resultSetElementId: element.id, resultSetId }, ResultSetElementType.ProcessDataViewer), calculationElementSelector(resultSetId, element.id), (simulations, optimizations, processData, processVariables, insights, recipes, cellModels, parameterTypes, processVariablesOptions, persistedInsightsData, persistedSimulationPreviews, workspaceName, incompleteSimulationsIds, { resultRows, calculationRows }, lineGraphResult, processDataViewerResult, calculationElement) => {
    let csvData = "";
    let fileName = "";
    const customVariables = getRecipesCustomVariables(recipes, GET_ALL_CUSTOM_VARIABLES);
    const getFileName = (defaultName) => `${getCurrentDateTimeStamp()}_${workspaceName}_${isNotEmpty(element.name, true) ? element.name : defaultName}`;
    switch (element.type) {
        case ResultSetElementType.Table:
            csvData = prepareTabularElementExportedData(element, simulations, recipes, cellModels, optimizations, processData, processVariables, insights, customVariables, resultRows ?? [], calculationRows, incompleteSimulationsIds);
            fileName = getFileName("Tabular view");
            break;
        case ResultSetElementType.LineGraph:
            csvData = prepareLineGraphElementExportedData(element, simulations, recipes, cellModels, parameterTypes ?? [], optimizations, processData, processVariables, insights, persistedInsightsData, persistedSimulationPreviews, customVariables, lineGraphResult ?? [], processVariablesOptions, calculationElement, incompleteSimulationsIds);
            fileName = getFileName("Line graph");
            break;
        case ResultSetElementType.ProcessDataViewer:
            csvData = prepareProcessDataViewerElementExportedData(element, simulations, recipes, cellModels, parameterTypes ?? [], optimizations, processData, processVariables, insights, persistedInsightsData, persistedSimulationPreviews, processDataViewerResult ?? [], processVariablesOptions, calculationElement, incompleteSimulationsIds);
            fileName = getFileName("Process data viewer");
            break;
    }
    return { csvData, fileName };
});
export const shouldInvokePrintResultSetSelector = createSelector(getWorkspaceState, (state) => state.shouldInvokePrintResultSet);
export const isResultElementChangingSelector = (resultSetId, elementId) => createSelector(resultSetByIdSelector(resultSetId), (resultSet) => !!resultSet?.elements.find(({ id }) => id === elementId)?.changing);
export const createEntityFromResultElementDialogDataSelector = createSelector(getWorkspaceState, (state) => state.createEntityFromResultElementDialogData);
